import { z } from 'zod';
import { ExpertTypeSchema, ListingTypeSchema, ShopifyPartnerTierSchema } from './shared';

export const PublicExpertListingCategorySchema = z.object({
    name: z.string(),
    slug: z.string(),
});

export const PublicExpertListingPackagedServiceSchema = z.object({
    name: z.string(),
    slug: z.string(),
});

export const PublicExpertListingBadgeSchema = z.object({
    name: z.string(),
    slug: z.string(),
    logo: z.string().nullable().optional(),
});

export const PublicExpertListingCitySchema = z.object({
    id: z.number().int(),
    name: z.string(),
    region: z.string().nullable().optional(),
});

export const PublicExpertListingCardSchema = z.object({
    id: z.number().int(),
    slug: z.string(),
    name: z.string(),
    title: z.string(),
    expert_type: ExpertTypeSchema,
    listing_type: ListingTypeSchema,
    avatar_url: z.string().nullable(),
    country: z.object({
        code: z.string(),
        name: z.string(),
    }).nullable(),
    city: PublicExpertListingCitySchema.nullable().optional().default(null),
    starting_price: z.object({
        amount: z.number(),
        currency: z.string(),
    }),
    is_pro: z.boolean(),
    is_claimed: z.boolean(),
    reviews: z.object({
        rating: z.number().nullable(),
        count: z.number().int().nonnegative(),
        source: z.enum(['shopexperts', 'partner']),
    }),
    confidence_score: z.object({
        score: z.number().min(0).max(100),
        earned_points: z.number().nonnegative(),
        available_points: z.number().nonnegative(),
    }),
    languages: z.array(z.string()).optional().default([]),
    categories: z.array(PublicExpertListingCategorySchema),
    packaged_services: z.array(PublicExpertListingPackagedServiceSchema).optional().default([]),
    badges: z.array(PublicExpertListingBadgeSchema).optional().default([]),
    description: z.string().nullable(),
});

export const PublicExpertListingMetaSchema = z.object({
    count: z.number().int().nonnegative(),
    review_stats: z.object({
        average_rating: z.number().min(0).max(5),
        reviews_count: z.number().int().nonnegative(),
    }),
    active_filters: z.object({
        expert_type: ListingTypeSchema.optional(),
        services_offered: z.array(z.string()).optional(),
        starting_price: z.object({
            from: z.number().optional(),
            to: z.number().optional(),
        }).optional(),
        countries: z.array(z.string()).optional(),
        cities: z.array(z.number().int()).optional(),
        languages: z.array(z.string()).optional(),
        apps_and_themes: z.array(z.string()).optional(),
        partner_tiers: z.array(ShopifyPartnerTierSchema).optional(),
        partner_badge: z.string().optional(),
        is_pro: z.boolean().optional(),
        claimed_only: z.boolean().optional(),
        search: z.string().optional(),
        ranking: z.enum(['all_time', 'most_recent']).optional(),
    }).optional(),
});

export type PublicExpertListingCard = z.infer<typeof PublicExpertListingCardSchema>;
export type PublicExpertListingMeta = z.infer<typeof PublicExpertListingMetaSchema>;
